import { useRef, useState } from 'react'
import type { FormEvent } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Card, CardBody, CardHeader } from '@/components/ui/Card'
import { useToast } from '@/components/ui/Toast'
import { SkeletonRows } from '@/components/ui/Skeleton'
import { EmptyState } from '@/components/ui/EmptyState'
import { Button, buttonClass } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { Select } from '@/components/ui/Select'
import { Textarea } from '@/components/ui/Textarea'
import { Sheet } from '@/components/ui/Sheet'
import { StatusChip } from '@/components/ui/StatusChip'
import { useProfile } from '@/auth/SessionProvider'
import { canWriteDepartment } from '@/auth/profile'
import { listCategories } from '@/data/budgets'
import { METHOD_LABELS } from '@/data/dues'
import type { PaymentMethod } from '@/data/dues'
import {
  PAID_FROM_LABELS, approveExpense, createExpense, deleteDraftExpense, listExpenses, listSpendableLines,
  markExpensePaid, recallExpense, rejectExpense, reopenRejectedExpense, reverseExpense, submitExpense,
  updateDraftExpense, withdrawApproval,
} from '@/data/expenses'
import type { Expense, ExpenseInput, PaidFrom } from '@/data/expenses'
import { formatDay as formatDate } from '@/lib/format'
import { formatKwacha } from '@/lib/money'
import { lusakaToday, quarterId } from '@/lib/periods'
import { preparePhoto } from '@/attachments/compress'
import type { PreparedPhoto } from '@/attachments/compress'
import { removeQueuedFor } from '@/attachments/queue'
import { queueProofPhoto } from '@/attachments/receipts'
import { OwnerDeleteButton } from '@/features/owner/OwnerDelete'
import { ProofPhotos } from '@/features/proofs/ProofPhotos'
import { amountToNgwee, expenseFormSchema, paidDateSchema, reasonSchema } from './schemas'

// The step that moves an expense forward is the main button in its row; the rest are quieter.
const FORWARD = new Set(['Send', 'Approve', 'Mark paid', 'Edit and send again'])
const errText = (e: unknown) => (e instanceof Error ? e.message : 'Something went wrong. Please try again.')
const toInput = (ngwee: number) => (ngwee % 100 === 0 ? String(ngwee / 100) : (ngwee / 100).toFixed(2))
const newRef = () => (globalThis.crypto?.randomUUID ? globalThis.crypto.randomUUID() : `x-${Date.now()}-${Math.random().toString(36).slice(2)}`)

type Act =
  | { kind: 'send' | 'markpaid'; e: Expense }
  | { kind: 'approve' | 'recall' | 'discard' | 'reopen'; e: Expense }
  | { kind: 'reject' | 'withdraw' | 'reverse'; e: Expense }

const ACT_TITLE: Record<Act['kind'], string> = {
  send: 'Send for approval',
  markpaid: 'Mark as paid',
  approve: 'Approve this expense?',
  recall: 'Take it back?',
  discard: 'Discard this draft?',
  reopen: 'Edit and send again?',
  reject: 'Send this expense back',
  withdraw: 'Withdraw the approval',
  reverse: 'Reverse this expense',
}
const ACT_BUTTON: Record<Act['kind'], string> = {
  send: 'Send for approval',
  markpaid: 'Mark as paid',
  approve: 'Approve',
  recall: 'Take it back',
  discard: 'Discard draft',
  reopen: 'Reopen as a draft',
  reject: 'Send back',
  withdraw: 'Withdraw approval',
  reverse: 'Reverse expense',
}
const needsReason = (k: Act['kind']) => k === 'reject' || k === 'withdraw' || k === 'reverse'

export function ExpensesPage({ deptId }: { deptId: string }) {
  const profile = useProfile()
  const canWrite = canWriteDepartment(profile, deptId)
  const isAdmin = profile.isAdmin
  const qc = useQueryClient()
  const today = lusakaToday()

  const expensesQ = useQuery({ queryKey: ['expenses', deptId], queryFn: () => listExpenses(deptId) })
  const catsQ = useQuery({ queryKey: ['categories'], queryFn: listCategories })

  const toast = useToast()
  const [actionError, setActionError] = useState('')

  // ---------------- the expense form (add when editing === null)
  const [formOpen, setFormOpen] = useState(false)
  const [editing, setEditing] = useState<Expense | null>(null)
  const [fDate, setFDate] = useState(today)
  const [fPayee, setFPayee] = useState('')
  const [fDesc, setFDesc] = useState('')
  const [fAmount, setFAmount] = useState('')
  const [fLine, setFLine] = useState('')
  const [fCat, setFCat] = useState('')
  const [fPaidFrom, setFPaidFrom] = useState<PaidFrom>('department_cash')
  const [fMethod, setFMethod] = useState<PaymentMethod>('cash')
  const [fReceipt, setFReceipt] = useState('')
  const [fAlreadyPaid, setFAlreadyPaid] = useState(false)
  const [fPaidOn, setFPaidOn] = useState(today)
  const [fErrors, setFErrors] = useState<Record<string, string>>({})
  const [fError, setFError] = useState('')
  const formBusy = useRef(false)
  const [photos, setPhotos] = useState<PreparedPhoto[]>([])
  const [photoBusy, setPhotoBusy] = useState(false)
  const [photoError, setPhotoError] = useState('')
  const photoInput = useRef<HTMLInputElement>(null)
  const clientRef = useRef(newRef())
  // Once this form has saved its expense, further taps CHANGE that draft rather than create another, so
  // corrections made after a failed send are not silently dropped.
  const savedDraftId = useRef<string | null>(null)
  const queuedPhotos = useRef(new Set<PreparedPhoto>())

  const quarter = /^\d{4}-\d{2}-\d{2}$/.test(fDate) ? quarterId(fDate) : ''
  const linesQ = useQuery({
    queryKey: ['spendable-lines', deptId, quarter],
    queryFn: () => listSpendableLines(deptId, quarter),
    enabled: formOpen && quarter !== '',
  })
  const lines = linesQ.data ?? []
  const chosenLine = lines.find((l) => l.id === fLine)

  const refresh = async () => {
    await qc.invalidateQueries({ queryKey: ['expenses', deptId] })
    await qc.invalidateQueries({ queryKey: ['spendable-lines', deptId] })
  }

  const openForm = (e: Expense | null) => {
    setEditing(e)
    setFDate(e?.incurredOn ?? today)
    setFPayee(e?.payee ?? '')
    setFDesc(e?.description ?? '')
    setFAmount(e ? toInput(e.amountNgwee) : '')
    setFLine(e?.budgetLineId ?? '')
    setFCat(e?.categoryId ?? '')
    setFPaidFrom(e?.paidFrom ?? 'department_cash')
    setFMethod(e?.method ?? 'cash')
    setFReceipt(e?.receiptNo ?? '')
    setFAlreadyPaid(false)
    setFPaidOn(today)
    setFErrors({})
    setFError('')
    setPhotos([])
    setPhotoError('')
    clientRef.current = newRef()
    savedDraftId.current = null
    queuedPhotos.current = new Set()
    setFormOpen(true)
  }

  const createM = useMutation({ mutationFn: (v: { input: ExpenseInput; ref: string }) => createExpense(deptId, v.input, v.ref) })
  const updateM = useMutation({ mutationFn: (v: { id: string; input: ExpenseInput }) => updateDraftExpense(v.id, v.input) })
  const sendM = useMutation({ mutationFn: (v: { id: string; paidOn: string | null }) => submitExpense(v.id, v.paidOn) })

  const pickPhotos = async (files: FileList | null) => {
    if (!files || files.length === 0) return
    setPhotoBusy(true)
    setPhotoError('')
    try {
      const prepared: PreparedPhoto[] = []
      for (const f of Array.from(files)) prepared.push(await preparePhoto(f))
      setPhotos((cur) => [...cur, ...prepared])
    } catch (e) {
      setPhotoError(errText(e))
    } finally {
      setPhotoBusy(false)
      if (photoInput.current) photoInput.current.value = ''
    }
  }

  /** `send` false saves a draft only; true also sends it for approval. */
  const submitForm = async (e: FormEvent, send: boolean) => {
    e.preventDefault()
    if (formBusy.current) return
    setFError('')
    const parsed = expenseFormSchema.safeParse({
      incurredOn: fDate, payee: fPayee, description: fDesc, amount: fAmount, budgetLineId: fLine, categoryId: fCat,
      paidFrom: fPaidFrom, method: fMethod, receiptNo: fReceipt, alreadyPaid: fAlreadyPaid, paidOn: fPaidOn,
    })
    const errs: Record<string, string> = {}
    if (!parsed.success) for (const i of parsed.error.issues) errs[String(i.path[0])] ??= i.message
    if (fAlreadyPaid && !paidDateSchema.safeParse(fPaidOn).success) errs.paidOn = 'Pick the date it was paid'
    if (parsed.success && parsed.data.incurredOn > today) errs.incurredOn = 'The date cannot be in the future'
    if (fAlreadyPaid && fPaidOn > today) errs.paidOn = 'The date cannot be in the future'
    setFErrors(errs)
    if (!parsed.success || Object.keys(errs).length > 0) return
    const v = parsed.data
    const input: ExpenseInput = {
      budgetLineId: v.budgetLineId === '' ? null : v.budgetLineId,
      categoryId: chosenLine ? chosenLine.categoryId : v.categoryId,
      payee: v.payee, description: v.description, amountNgwee: amountToNgwee(v.amount) as number,
      incurredOn: v.incurredOn, paidFrom: v.paidFrom, method: v.method, receiptNo: v.receiptNo,
    }
    formBusy.current = true
    let savedId: string | null = null
    try {
      const existing = editing?.id ?? savedDraftId.current
      if (existing) {
        await updateM.mutateAsync({ id: existing, input })
        savedId = existing
      } else {
        savedId = await createM.mutateAsync({ input, ref: clientRef.current })
        savedDraftId.current = savedId
      }
      // The expense row is saved. Only NOW do the photos go on the queue, and they upload in the
      // background: nothing below waits for the network.
      for (const p of photos) {
        if (queuedPhotos.current.has(p)) continue // already on the queue from an earlier tap
        await queueProofPhoto(p, { departmentId: deptId, parent: { kind: 'expense', id: savedId }, date: v.incurredOn })
        queuedPhotos.current.add(p)
        setPhotos((cur) => cur.filter((x) => x !== p)) // it is on the queue now: do not show it as still to come
      }
      if (send) await sendM.mutateAsync({ id: savedId, paidOn: fAlreadyPaid ? fPaidOn : null })
      await refresh()
      setFormOpen(false)
      toast.success(send ? 'The expense was sent for approval.' : 'The expense was saved as a draft. Send it when you are ready.')
    } catch (err) {
      if (send && savedDraftId.current && /changed since you opened it/i.test(errText(err))) {
        // The send actually went through earlier (its answer was lost): nothing is wrong, so say so.
        await refresh()
        setFormOpen(false)
        toast.success('The expense was already sent for approval.')
        formBusy.current = false
        return
      }
      setFError(
        savedId && send
          ? `The expense was saved as a draft, but could not be sent: ${errText(err)}`
          : errText(err),
      )
      if (savedId) {
        // Keep the form pointing at the saved row so another try does not make a second one.
        void refresh()
      }
    } finally {
      formBusy.current = false
    }
  }

  // ---------------- row actions
  const [act, setAct] = useState<Act | null>(null)
  const [reason, setReason] = useState('')
  const [reasonError, setReasonError] = useState('')
  const [paidDate, setPaidDate] = useState(today)
  const [dateError, setDateError] = useState('')

  const openAct = (a: Act) => {
    setActionError('')
    setReason('')
    setReasonError('')
    setPaidDate(a.kind === 'markpaid' ? today : '')
    setDateError('')
    setAct(a)
  }

  const actM = useMutation({
    mutationFn: async (a: { act: Act; reason: string; paidOn: string }) => {
      const id = a.act.e.id
      switch (a.act.kind) {
        case 'send': return submitExpense(id, a.paidOn === '' ? null : a.paidOn)
        case 'markpaid': return markExpensePaid(id, a.paidOn)
        case 'approve': return approveExpense(id)
        case 'recall': return recallExpense(id)
        case 'discard': return deleteDraftExpense(id)
        case 'reopen': return reopenRejectedExpense(id)
        case 'reject': return rejectExpense(id, a.reason)
        case 'withdraw': return withdrawApproval(id, a.reason)
        case 'reverse': return reverseExpense(id, a.reason)
      }
    },
  })

  const DONE: Record<Act['kind'], string> = {
    send: 'The expense was sent for approval.',
    markpaid: 'The expense is marked as paid.',
    approve: 'The expense is approved.',
    recall: 'You took the expense back. You can edit it and send it again.',
    discard: 'The draft was discarded.',
    reopen: 'The expense is a draft again. Edit it and send it again.',
    reject: 'The expense was sent back with your reason.',
    withdraw: 'The approval was withdrawn.',
    reverse: 'A matching negative entry was added. The original is marked void.',
  }

  const runAct = async (e: FormEvent) => {
    e.preventDefault()
    if (!act || actM.isPending) return
    let paidOn = ''
    if (act.kind === 'markpaid') {
      if (!paidDateSchema.safeParse(paidDate).success) return setDateError('Pick the date it was paid')
      if (paidDate > today) return setDateError('The date cannot be in the future')
      paidOn = paidDate
    }
    if (act.kind === 'send' && paidDate !== '') {
      if (!paidDateSchema.safeParse(paidDate).success) return setDateError('Pick the date it was paid')
      if (paidDate > today) return setDateError('The date cannot be in the future')
      paidOn = paidDate
    }
    if (needsReason(act.kind)) {
      const p = reasonSchema.safeParse({ reason })
      if (!p.success) return setReasonError(p.error.issues[0].message)
    }
    setActionError('')
    try {
      await actM.mutateAsync({ act, reason, paidOn })
      if (act.kind === 'discard') await removeQueuedFor(act.e.id) // its waiting photos have nowhere to go now
      await refresh()
      toast.success(DONE[act.kind])
      setAct(null)
    } catch (err) {
      setActionError(errText(err))
      void refresh()
    }
  }

  const expenses = expensesQ.data ?? []
  const cats = catsQ.data ?? []
  const catName = (id: string) => cats.find((c) => c.id === id)?.name ?? ''

  const actionsFor = (x: Expense): { label: string; tone?: 'danger'; run: () => void }[] => {
    if (x.reversesId || x.status === 'void') return []
    const out: { label: string; tone?: 'danger'; run: () => void }[] = []
    if (canWrite && x.status === 'draft') {
      out.push({ label: 'Send', run: () => openAct({ kind: 'send', e: x }) })
      out.push({ label: 'Edit', run: () => openForm(x) })
      out.push({ label: 'Discard', tone: 'danger', run: () => openAct({ kind: 'discard', e: x }) })
    }
    if (canWrite && x.status === 'submitted') out.push({ label: 'Take back', run: () => openAct({ kind: 'recall', e: x }) })
    if (canWrite && x.status === 'rejected') out.push({ label: 'Edit and send again', run: () => openAct({ kind: 'reopen', e: x }) })
    if (isAdmin && x.status === 'submitted') {
      out.push({ label: 'Approve', run: () => openAct({ kind: 'approve', e: x }) })
      out.push({ label: 'Send back', tone: 'danger', run: () => openAct({ kind: 'reject', e: x }) })
    }
    if (isAdmin && x.status === 'approved') {
      out.push({ label: 'Mark paid', run: () => openAct({ kind: 'markpaid', e: x }) })
      out.push({ label: 'Withdraw approval', tone: 'danger', run: () => openAct({ kind: 'withdraw', e: x }) })
    }
    if (isAdmin && x.status === 'paid') out.push({ label: 'Reverse', tone: 'danger', run: () => openAct({ kind: 'reverse', e: x }) })
    return out
  }

  const busy = actM.isPending
  const saving = createM.isPending || updateM.isPending || sendM.isPending

  return (
    <div className="flex flex-col gap-6">
      <div>
        <h2 className="text-title font-semibold text-ink">Expenses</h2>
        <p className="mt-1 text-muted">Money the department has spent or plans to spend.</p>
      </div>

      {actionError && !act && <p role="alert" className="rounded-control bg-bad-tint px-4 py-3 text-body text-bad">{actionError}</p>}

      <Card>
        <CardHeader className="flex items-center justify-between gap-3">
          <h3 className="font-semibold text-ink">All expenses</h3>
          {canWrite && <Button size="lg" onClick={() => openForm(null)}>Record an expense</Button>}
        </CardHeader>
        <CardBody>
          {expensesQ.isPending ? (
            <SkeletonRows rows={4} />
          ) : expensesQ.isError ? (
            <div>
              <p role="alert" className="text-body text-bad">{errText(expensesQ.error)}</p>
              <Button variant="secondary" size="lg" className="mt-3" onClick={() => void expensesQ.refetch()}>Try again</Button>
            </div>
          ) : expenses.length === 0 ? (
            <EmptyState title="No expenses yet" description={canWrite ? 'Record the first one when money is spent.' : 'Nothing has been recorded.'} />
          ) : (
            <ul className="divide-y divide-line">
              {expenses.map((x) => {
                const acts = actionsFor(x)
                return (
                  <li key={x.id} className="py-3">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <p className="font-medium text-ink">{x.payee}</p>
                        <p className="text-body text-neutral">{x.description}</p>
                        <p className="text-body text-muted">
                          {formatDate(x.incurredOn)} · {x.categoryName}
                          {x.budgetLineName ? ` · ${x.budgetLineName}` : ' · Not in the budget'}
                        </p>
                        {x.paidOn && <p className="text-micro text-muted">Paid {formatDate(x.paidOn)}, {METHOD_LABELS[x.method]}, {PAID_FROM_LABELS[x.paidFrom]}</p>}
                        {x.receiptNo && <p className="text-micro text-muted">Receipt {x.receiptNo}</p>}
                        {x.reversesId && <p className="text-micro text-muted">Correction: {x.reversalReason}</p>}
                        {x.status === 'rejected' && x.decisionNote && <p className="text-body text-wait">Sent back: {x.decisionNote}</p>}
                      </div>
                      <div className="flex shrink-0 flex-col items-end gap-1">
                        <span className="font-medium text-ink">{formatKwacha(x.amountNgwee)}</span>
                        <StatusChip status={x.status} />
                      </div>
                    </div>
                    <ProofPhotos parent={{ kind: 'expense', id: x.id }} departmentId={x.departmentId} date={x.incurredOn} canAdd={canWrite && x.status !== 'void' && !x.reversesId} />
                    {!x.reversesId && x.amountNgwee > 0 && (
                      <OwnerDeleteButton table="expense" id={x.id} what="expense" wrap="mt-1 flex flex-wrap gap-2" />
                    )}
                    {acts.length > 0 && (
                      <div className="mt-1 flex flex-wrap gap-1">
                        {acts.map((a) => (
                          <button
                            key={a.label}
                            type="button"
                            onClick={a.run}
                            className={buttonClass(a.tone === 'danger' ? 'ghost' : FORWARD.has(a.label) ? 'primary' : 'secondary', 'sm', a.tone === 'danger' ? 'text-bad hover:bg-bad-tint' : undefined)}
                          >
                            {a.label}
                          </button>
                        ))}
                      </div>
                    )}
                  </li>
                )
              })}
            </ul>
          )}
        </CardBody>
      </Card>

      <Sheet isOpen={formOpen} onClose={() => setFormOpen(false)} title={editing ? 'Change this expense' : 'Record an expense'}>
        <form onSubmit={(e) => void submitForm(e, true)} className="flex flex-col gap-4" noValidate>
          <Input label="Date of the expense" type="date" max={today} value={fDate} onChange={(e) => { setFDate(e.target.value); setFLine('') }} error={fErrors.incurredOn} />
          <Input label="Who was paid?" placeholder="Town Hall" value={fPayee} onChange={(e) => setFPayee(e.target.value)} error={fErrors.payee} />
          <Input label="What was it for?" placeholder="Hall hire for the youth camp" value={fDesc} onChange={(e) => setFDesc(e.target.value)} error={fErrors.description} />
          <Input label="Amount in Kwacha" inputMode="decimal" placeholder="500.00" value={fAmount} onChange={(e) => setFAmount(e.target.value)} error={fErrors.amount} />
          <Select
            label="Which part of the budget?"
            value={fLine}
            onChange={(e) => setFLine(e.target.value)}
            options={[
              { value: '', label: 'Not in the budget' },
              ...lines.map((l) => ({ value: l.id, label: `${l.description} (${formatKwacha(l.remainingNgwee)} left)` })),
            ]}
            hint={
              fLine === ''
                ? lines.length === 0 && !linesQ.isPending
                  ? 'There is no approved budget for this date, so this is recorded as unbudgeted spending and needs approval.'
                  : 'Spending outside the budget always needs approval.'
                : undefined
            }
          />
          <Select
            label="Category"
            value={chosenLine ? chosenLine.categoryId : fCat}
            disabled={Boolean(chosenLine)}
            onChange={(e) => setFCat(e.target.value)}
            options={[{ value: '', label: 'Choose a category' }, ...cats.map((c) => ({ value: c.id, label: c.name }))]}
            hint={chosenLine ? `Set by the budget line: ${catName(chosenLine.categoryId)}` : undefined}
            error={fErrors.categoryId}
          />
          <Select
            label="Paid with"
            value={fMethod}
            onChange={(e) => setFMethod(e.target.value as PaymentMethod)}
            options={(Object.keys(METHOD_LABELS) as PaymentMethod[]).map((m) => ({ value: m, label: METHOD_LABELS[m] }))}
          />
          <Select
            label="Where did the money come from?"
            value={fPaidFrom}
            onChange={(e) => setFPaidFrom(e.target.value as PaidFrom)}
            options={(Object.keys(PAID_FROM_LABELS) as PaidFrom[]).map((m) => ({ value: m, label: PAID_FROM_LABELS[m] }))}
          />
          <Input label="Receipt number (optional)" value={fReceipt} onChange={(e) => setFReceipt(e.target.value)} error={fErrors.receiptNo} />
          <div className="flex flex-col gap-1.5">
            <span className="text-body font-medium text-neutral">Photo of the receipt (optional)</span>
            <input
              ref={photoInput}
              type="file"
              accept="image/*"
              capture="environment"
              multiple
              className="sr-only"
              aria-label="Photo of the receipt"
              onChange={(e) => void pickPhotos(e.target.files)}
            />
            <div className="flex flex-wrap items-center gap-2">
              <Button type="button" variant="secondary" size="lg" disabled={photoBusy} onClick={() => photoInput.current?.click()}>
                {photoBusy ? 'Preparing photo...' : photos.length === 0 ? 'Take or choose a photo' : 'Add another photo'}
              </Button>
              {photos.length > 0 && (
                <span className="text-body text-neutral">
                  {photos.length} {photos.length === 1 ? 'photo' : 'photos'} ready
                  <button type="button" className="ml-2 inline-flex min-h-[44px] items-center px-2 text-body font-medium text-bad" onClick={() => setPhotos([])}>Remove</button>
                </span>
              )}
            </div>
            <span className="text-body text-muted">The expense is saved first. The photo uploads in the background, even if the connection is slow.</span>
            {photoError && <span role="alert" className="text-body text-bad">{photoError}</span>}
          </div>
          <label className="flex min-h-[44px] items-center gap-3 text-body font-medium text-neutral">
            <input type="checkbox" className="h-5 w-5" checked={fAlreadyPaid} onChange={(e) => setFAlreadyPaid(e.target.checked)} />
            The money has already been paid out
          </label>
          {fAlreadyPaid && <Input label="Date it was paid" type="date" max={today} value={fPaidOn} onChange={(e) => setFPaidOn(e.target.value)} error={fErrors.paidOn} />}
          {fError && <p role="alert" className="text-body text-bad">{fError}</p>}
          <Button type="submit" size="lg" isLoading={saving}>{saving ? 'Saving...' : 'Save and send for approval'}</Button>
          <Button type="button" variant="secondary" size="lg" disabled={saving} onClick={(e) => void submitForm(e, false)}>Save as a draft</Button>
        </form>
      </Sheet>

      <Sheet isOpen={act !== null} onClose={() => setAct(null)} title={act ? ACT_TITLE[act.kind] : ''}>
        <form onSubmit={(e) => void runAct(e)} className="flex flex-col gap-4" noValidate>
          {act && (
            <p className="text-neutral">
              {act.e.payee}, {formatKwacha(act.e.amountNgwee)}: {act.e.description}.
            </p>
          )}
          {act?.kind === 'send' && (
            <>
              <Input label="Date it was paid (leave empty if not paid yet)" type="date" max={today} value={paidDate} onChange={(e) => setPaidDate(e.target.value)} error={dateError} />
            </>
          )}
          {act?.kind === 'markpaid' && <Input label="Date it was paid" type="date" max={today} value={paidDate} onChange={(e) => setPaidDate(e.target.value)} error={dateError} />}
          {act && needsReason(act.kind) && (
            <Textarea
              label={act.kind === 'reject' ? 'What needs to change?' : 'Why?'}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              error={reasonError}
              rows={3}
            />
          )}
          {act?.kind === 'reverse' && <p className="text-body text-muted">Nothing is deleted: a matching negative entry is added and the original is marked void.</p>}
          {actionError && <p role="alert" className="text-body text-bad">{actionError}</p>}
          <Button type="submit" size="lg" variant={act && ['discard', 'reject', 'withdraw', 'reverse'].includes(act.kind) ? 'danger' : 'primary'} isLoading={busy}>
            {act ? ACT_BUTTON[act.kind] : ''}
          </Button>
          <Button type="button" variant="secondary" size="lg" onClick={() => setAct(null)}>Cancel</Button>
        </form>
      </Sheet>
    </div>
  )
}
